import React, { useState, useMemo } from 'react';
import { shape, string, arrayOf } from 'prop-types';
import classNames from 'classnames/bind';

import { QueryProvider } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { Image, Carousel } from '@thd-olt-component-react/core-ui';
import { ProductPod, ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';

import { useSpecialBuy } from '../../context/SpecialBuyProvider';
import { useSearchProducts, MAX_NUM_ITEMS_TO_LOAD } from '../../hooks/useSearchProducts';
import styles from './category-products.module.scss';

const cNames = classNames.bind(styles);

const optionsHandler = ({ options }) => {
  return {
    ...options,
    fetchPolicy: 'cache-only'
  };
};

const buildProductPodArray = ({
  storeId, itemIdsToDisplay, itemIdsWithSwatches, categoryName, specialBuyType
}) => {
  const analyticsData = {
    section: specialBuyType === 'sbotd'
      ? 'SBOTD banner'
      : 'SBOTW banner',
    sharedSection: categoryName,
  };

  return itemIdsToDisplay.map((itemId, index) => {
    const productPodClassNames = cNames('special-buy-category__product-pod', {
      'see-more-options--hidden': !itemIdsWithSwatches.has(itemId),
    });

    return (
      <ProductPod
        className={productPodClassNames}
        itemId={itemId}
        storeId={storeId}
        titleMaxLine={2}
        brandMaxLine={1}
        hideFavorites
        hidePriceBadge
        hideShippingInfo
        hideShopThisCollection
        hideStoreInfo
        hideFulfillment
        hidePromotions
        showExactPriceOnLoad
        showSeeMoreOptions
        doNotShowChooseYourOptions
        position={index}
        key={`special-buy-${itemId}`}
        analyticsData={analyticsData}
      />
    );
  });
};

const CategoryProducts = ({ category }) => {
  const { selectedCategory, specialBuyType, specialBuyEndpoint, simpleBanner } = useSpecialBuy();
  const { storeId } = useStore();
  const [isFetchAllItemIds, setIsFetchAllItemIds] = useState(false);
  const isSelected = Object.is(selectedCategory, category);

  const {
    itemIdsToDisplay,
    isLoading,
  } = useSearchProducts(category, isFetchAllItemIds);

  const carouselContent = useMemo(() => {
    if (!itemIdsToDisplay?.length) return [];
    const newCarouselContent = [];

    const itemIdsWithSwatches = new Set();
    category.products.slice(0, MAX_NUM_ITEMS_TO_LOAD).forEach((product) => {
      if (product.hasSwatches) itemIdsWithSwatches.add(product.itemId);
    });

    const productPods = buildProductPodArray({
      itemIdsToDisplay,
      storeId,
      itemIdsWithSwatches,
      categoryName: category.name,
      specialBuyType,
    });

    newCarouselContent.push(...productPods);

    const maxPodsToShow = Math.min(category.products.length, MAX_NUM_ITEMS_TO_LOAD);
    const numPlaceholderPods = category.products.length - Math.max(maxPodsToShow - itemIdsToDisplay.length, 0);
    const placeholderPods = category.products.slice(numPlaceholderPods).map((product) => (
      <ProductPodPlaceholder podsCount={1} key={`placeholder-pod-${product.itemId}`} />
    ));

    if (!isFetchAllItemIds || isLoading) newCarouselContent.push(...placeholderPods);

    if (category.products.length > MAX_NUM_ITEMS_TO_LOAD && !simpleBanner) {
      newCarouselContent.push(
        <div
          className={cNames('special-buy-category__see-all')}
          key={`see-all-${category.categoryId}`}
          // id property is required by analytics
          id={specialBuyType === 'sbotd' ? 'SBOTD' : 'SBOTW'}
        >
          <a className={cNames('u__bold')} href={`${specialBuyEndpoint}#${category.categoryId}`}>
            See All {category.name} {{
              sbotd: 'Special Buy Of The Day',
              decor: 'Decor Special Buy Of The Week',
              pro: 'Pro Special Buy Of The Week',
            }[specialBuyType]}&nbsp;
            <Image src="https://assets.thdstatic.com/images/v1/caret-orange.svg" alt="" />
          </a>
        </div>,
      );
    }

    return newCarouselContent;
  }, [
    itemIdsToDisplay,
    storeId,
    isLoading,
    isFetchAllItemIds,
    category.categoryId,
    category.name,
    category.products,
    specialBuyEndpoint,
    specialBuyType,
    simpleBanner
  ]);

  const categoryClassNames = cNames('special-buy-category', {
    'u--hide': !isSelected,
  });

  const onSlideNextTransitionStart = !isFetchAllItemIds
    ? () => { setIsFetchAllItemIds(true); }
    : undefined;

  return (
    <article className={categoryClassNames}>
      {carouselContent.length ? (
        <QueryProvider
          queryOptions={optionsHandler}
          defaultVariables={{ storeId, loyaltyMembershipInput: null }}
          cacheKey="special-buy-carousel"
          dataSource="searchNav"
        >
          <Carousel onSlideNextTransitionStart={onSlideNextTransitionStart}>
            {carouselContent}
          </Carousel>
        </QueryProvider>
      ) : (
        <ProductPodPlaceholder podsCount={5} />
      )}
    </article>
  );
};

CategoryProducts.displayName = 'CategoryProducts';

CategoryProducts.propTypes = {
  category: shape({
    name: string.isRequired,
    products: arrayOf(shape({ image: string.isRequired })).isRequired,
    categoryId: string.isRequired,
  }).isRequired
};

export { CategoryProducts };
